import React, { useContext, useRef } from 'react';
import { ReactComponent as ManageSearch } from '../../../../../../assets/images/manage_search.svg';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { Button, Checkbox, FormControlLabel, MenuItem, Select, Typography } from '@material-ui/core';

import { SortableItemContext } from '../ContentItem';
import SearchFilterModal from '../../../../../../reusable/SearchFilterModal';
import { searchFilterDisplayTypes, searchFilterTitleTypes } from '../../../../../../constants/portalThemesConstant';
import { useTranslation } from 'react-i18next';

type SearchFilterContentProps = {
	item: any;
};

const SearchFilterContent = ({ item }: SearchFilterContentProps) => {
	const { t } = useTranslation();
	const { id, data, displayType, rows, isShowTitle, showTitleType, isOpenDefault } = item;
	const {
		ref,
		attributes,
		listeners,
		expandedId,
		accountPreset,
		handleUpdateSearchFilter,
		handleDeleteContentItem,
		handleDuplicateContentItem,
		setExpandedId,
	} = useContext(SortableItemContext);

	const isExpand = expandedId === id;

	const searchFilterModalRef = useRef<any>();

	return (
		<>
			<div
				{...attributes}
				{...listeners}
				ref={ref}
				style={{
					border: '1px solid #909CA5',
					borderTopLeftRadius: 4,
					borderTopRightRadius: 4,
					borderBottomLeftRadius: isExpand ? 0 : 4,
					borderBottomRightRadius: isExpand ? 0 : 4,
					cursor: 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
				onClick={() => {
					setExpandedId(expandedId === id ? '' : id);
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'start',
						alignItems: 'center',
						gap: 10,
						padding: '14px 0 14px 10px',
					}}
				>
					<ManageSearch style={{ width: '18px', height: '18px' }} />
					<Typography variant="body1">
						{`${data.name || t('MEDIA_LIBRARY_SIDEBAR_SEARCH_FILTERS_LABEL')}`}
					</Typography>
				</div>
				<div style={{ display: 'flex' }}>
					<Button
						style={{ minWidth: 0, padding: '14px 10px', borderRadius: 0, borderLeft: '1px solid #909CA5' }}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							handleDuplicateContentItem(id);
						}}
					>
						<FileCopyIcon fontSize="small" />
					</Button>
					<Button
						style={{ minWidth: 0, padding: '14px 10px', borderRadius: 0, borderLeft: '1px solid #909CA5' }}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							handleDeleteContentItem(id);
						}}
					>
						<CloseIcon fontSize="small" />
					</Button>
				</div>
			</div>
			{isExpand && (
				<div
					style={{
						borderLeft: '1px solid #909CA5',
						borderRight: '1px solid #909CA5',
						borderBottom: '1px solid #909CA5',
						borderTopLeftRadius: 0,
						borderTopRightRadius: 0,
						borderBottomLeftRadius: 4,
						borderBottomRightRadius: 4,
						padding: '14px 10px',
					}}
				>
					<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
						<Button
							variant="contained"
							size="small"
							color="primary"
							style={{ minWidth: 0 }}
							onClick={() => searchFilterModalRef?.current?.showMediaLibrary(true)}
						>
							{data.name ? "Edit filter" : "Create new filter"}
						</Button>
					</div>
					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex' }}
						control={
							<Select
								value={displayType}
								variant="outlined"
								fullWidth
								onChange={(
									event: React.ChangeEvent<{
										name?: string;
										value: unknown;
									}>
								) => {
									const value = event.target.value as string;
									handleUpdateSearchFilter(id, 'displayType', value);
									if (value === 'carousel') {
										handleUpdateSearchFilter(id, 'rows', 1);
									}
									if (value === 'grid') {
										handleUpdateSearchFilter(id, 'showTitleType', 'normal');
									}

									if (value === 'storyCarousel') {
										handleUpdateSearchFilter(id, 'showTitleType', 'normal');
										handleUpdateSearchFilter(id, 'rows', 1);
									}
								}}
							>
								{searchFilterDisplayTypes.map(({ key, label }: any) => {
									if (key === 'storyCarousel' && !(accountPreset ?? []).includes('Story')) {
										return;
									}
									return (
										<MenuItem key={key} value={key}>
											{label}
										</MenuItem>
									);
								})}
							</Select>
						}
						label={
							<span className={'inspect-media__input-label'}>
								<Typography variant="body1">Display</Typography>
							</span>
						}
						labelPlacement={'start'}
					/>

					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex' }}
						control={
							<Select
								value={rows}
								variant="outlined"
								fullWidth
								disabled={displayType === 'carousel' || displayType === 'storyCarousel'}
								onChange={(
									event: React.ChangeEvent<{
										name?: string;
										value: unknown;
									}>
								) => {
									const value = event.target.value as string;
									handleUpdateSearchFilter(id, 'rows', value);
								}}
							>
								{[1, 2, 3, 4, 5].map((value: number) => {
									return (
										<MenuItem key={value} value={value}>
											{value}
										</MenuItem>
									);
								})}
							</Select>
						}
						label={
							<span className={'inspect-media__input-label'}>
								<Typography variant="body1">Rows</Typography>
							</span>
						}
						labelPlacement={'start'}
					/>

					<FormControlLabel
						className={'inspect-media__input--full'}
						style={{ display: 'flex' }}
						control={
							<Select
								value={showTitleType}
								variant="outlined"
								fullWidth
								disabled={displayType === 'grid' || displayType === 'storyCarousel'}
								onChange={(
									event: React.ChangeEvent<{
										name?: string;
										value: unknown;
									}>
								) => {
									const value = event.target.value as string;
									handleUpdateSearchFilter(id, 'showTitleType', value);
								}}
							>
								{searchFilterTitleTypes.map(({ key, label }: any) => {
									return (
										<MenuItem key={key} value={key}>
											{label}
										</MenuItem>
									);
								})}
							</Select>
						}
						label={
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<Checkbox
									id={`${id}-showTitle`}
									color="primary"
									checked={isShowTitle}
									size="small"
									onChange={(_, checked) => {
										handleUpdateSearchFilter(id, 'isShowTitle', checked);
									}}
								/>
								<label
									htmlFor={`${id}-showTitle`}
									style={{ color: 'rgba(0, 0, 0, 0.87)', fontWeight: '400' }}
								>
									Show title
								</label>
							</div>
						}
						labelPlacement={'start'}
					/>
				</div>
			)}
			<SearchFilterModal
				item={data}
				isOpenDefault={isOpenDefault}
				hiddenStockLibrary
				hiddenInteractLibrary={false}
				ref={searchFilterModalRef}
				handleReturnedMedia={(data) => {
					handleUpdateSearchFilter(id, 'data', data);
				}}
			/>
		</>
	);
};

export default SearchFilterContent;
